import { useCallback, useMemo } from 'react';

import { useTheme } from '@mui/material';

import { useChartContext } from '@/components/Chart/context/ChartContext';
import { createChartAppearance } from '@/components/Chart/theme/chartAppearance';
import type { ExtendedSeriesType } from '@/components/Chart/types/highcharts';
import { exportChartImage } from '@/components/Chart/utils/exportChartImage';
import getUnitSuffix from '@/components/Chart/utils/getUnitSuffix';
import { Permission } from '@/entities/permissions';
import { useExportNameMeta } from '@/features/DataHub/modules/Export/context/exportContexts';
import { buildInitialExportName } from '@/features/DataHub/modules/Export/services/ExportNameService';
import usePermission from '@/shared/hooks/usePermission';

import type { ExportingMimeTypeValue, Options } from 'highcharts';

const CAIRO_FONT_URL = '/fonts/Cairo.ttf';
const DEFAULT_EXPORT_FILENAME = 'GX-Chart';

const getSeriesNamesWithUnits = (series: ExtendedSeriesType[]) =>
  series.map(s => ({ name: `${s.name}${getUnitSuffix(s.userOptions.custom)}` }));

const useChartImageExport = () => {
  const { chartRef } = useChartContext();
  const { exportName } = useExportNameMeta();
  const theme = useTheme();
  const { hasPermission } = usePermission();
  const useSmallWatermark = hasPermission(Permission.UseSmallChartWatermark);
  const exportAppearance = useMemo(() => createChartAppearance(theme, 'export'), [theme]);

  const watermarkOptions = useMemo(
    () => ({
      watermarkName: exportAppearance.watermarkName,
      useSmallWatermark
    }),
    [exportAppearance.watermarkName, useSmallWatermark]
  );

  const onExport = useCallback(
    (type: ExportingMimeTypeValue): Promise<void> => {
      const chart = chartRef.current?.chart;
      if (!chart) return Promise.resolve();

      const baseOptions = chart.options.exporting?.chartOptions ?? chart.options;
      const baseHighchartsOptions = baseOptions as Options;

      const exportingOptions =
        type === 'application/pdf'
          ? {
              local: true,
              sourceWidth: 1200,
              pdfFont: {
                normal: CAIRO_FONT_URL,
                bold: CAIRO_FONT_URL,
                italic: CAIRO_FONT_URL,
                bolditalic: CAIRO_FONT_URL
              }
            }
          : { sourceWidth: 1200 };

      return exportChartImage({
        chart,
        type,
        filename: buildInitialExportName(exportName ?? '', DEFAULT_EXPORT_FILENAME),
        options: {
          ...baseOptions,
          chart: {
            ...baseHighchartsOptions.chart
          },
          legend: {
            ...baseHighchartsOptions.legend,
            layout: 'horizontal',
            align: 'center',
            verticalAlign: 'bottom',
            padding: 20,
            itemWidth: undefined,
            itemStyle: {
              ...baseHighchartsOptions.legend?.itemStyle,
              fontSize: '10px'
            }
          },
          series: getSeriesNamesWithUnits(chart.series as ExtendedSeriesType[]),
          // Pass export type so SVG post-processing and Highcharts conversion use the same format.
          exporting: { type }
        },
        exportingOptions,
        watermarkOptions
      });
    },
    [chartRef, exportName, watermarkOptions]
  );

  return { onExport };
};

export default useChartImageExport;
